<template>
	<div>
		<EwInput
			v-if="props.inputType === 'input' || props.inputType === 'packageTitle'"
			ref="inputElement"
			class="min-w-[600px]"
			:clearable="false"
			v-model="stringContent"
			:disabled="props.disabled"
		>
		</EwInput>
		<EwTextarea
			v-if="props.inputType === 'textarea'"
			ref="inputElement"
			:clearable="false"
			v-model="stringContent"
			class="min-w-[700px] w-full"
			rows="5"
			v-bind="textareaProps"
			:disabled="props.disabled"
		/>
		<EwDatepicker
			v-else-if="props.inputType === 'datepicker'"
			v-model="dateContent"
			inline
			dateFormat="dd.mm.yy"
			:clearable="false"
			:disabled="props.disabled"
		/>
		<EditorComponent
			v-else-if="props.inputType === 'editor'"
			ref="editorRef"
			v-model="stringContent"
			:disabled="props.disabled"
			class="min-w-[740px] w-full"
			editorStyle="height: 150px"/>
		<DropdownStatus
			v-else-if="props.inputType === 'dropdown'"
			v-model="content"
			:disabled="props.disabled"
			v-bind="dropdownProps"
		/>
		<EwSelect
			v-else-if="props.inputType === 'select'"
			v-model="content"
			v-bind="selectProps"
			:options="selectOptions"
			:disabled="props.disabled"
			:clearable="false"
			appendTo="self"
		/>
		<InplaceReference
			v-else-if="props.inputType === 'references'"
			ref="inputElement"
			v-model="referenceContent"
			class="min-w-[600px]"
			:disabled="props.disabled"
		/>
		<InplaceQuestions
			v-else-if="props.inputType === 'quiz'"
			ref="inputElement"
			v-model="questionsContent"
			class="min-w-[700px] max-w-[740px] w-full"
			:disabled="props.disabled"
		/>
		<small v-if="props.inputType !== 'select' && props.inputType !== 'datepicker' && props.inputType !== 'references' && props.inputType !== 'dropdown' && props.inputType !== 'editor' && props.inputType !== 'quiz'" 
		class="!color-[#64748b]"> "<strong>CTRl + ,</strong>" for subscript, "<strong>CTRl + .</strong>" for superscript</small>

	</div>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon';
import { EwInput, EwTextarea, EwDatepicker, EwSelect } from '@arkit/ew-vue-components';
import type { InplaceInputProps } from './';
import { CVDataProvided } from '@/types/cvTypes';
import InplaceReference from './InplaceReference.vue';
import type { QuizQuestionsItem, ReferencePostItem } from '@/types';
import InplaceQuestions from './InplaceQuestions.vue';
import EditorComponent from './EditorComponent.vue';
import { computed, inject, onMounted, onBeforeUnmount, onUpdated, ref } from 'vue';

const props = withDefaults(defineProps<InplaceInputProps>(), {
	disabled: false,
});

const content = defineModel<string | ReferencePostItem | QuizQuestionsItem>('content', {
	required: true,
});

const stringContent = computed({
	get: () => content.value as string,
	set: (value) => { content.value = value; },
});

const dateContent = computed({
	get: () => new Date(content.value.toString()),
	set: (value) => { content.value = DateTime.fromJSDate(value).toISODate() ?? ''; },
});

const referenceContent = computed({
	get: () => content.value as ReferencePostItem,
	set: (value) => { content.value = value; },
});

const questionsContent = computed({
	get: () => content.value as QuizQuestionsItem,
	set: (value) => { content.value = value; },
});

const CVData = inject(CVDataProvided);

const selectOptions = computed(() => {
	if (props.inputType !== 'select') return [];
	if (props.selectProps?.options) return props.selectProps.options;
	if (CVData && props.selectProps?.optionsCvKey) return CVData.value.data[props.selectProps.optionsCvKey];
	return [];
});


// ===================================================
//   SUPERSCRIPT & SUBSCRIPT SUPPORT FOR EW-INPUT
// ===================================================
const inputElement = ref<any>(null);

// Superscript mapping (lowercase + uppercase where Unicode exists)
const superscriptMap: Record<string, string> = {
	"0":"⁰","1":"¹","2":"²","3":"³","4":"⁴","5":"⁵","6":"⁶","7":"⁷","8":"⁸","9":"⁹",
	"a":"ᵃ","b":"ᵇ","c":"ᶜ","d":"ᵈ","e":"ᵉ","f":"ᶠ","g":"ᵍ","h":"ʰ","i":"ⁱ","j":"ʲ",
	"k":"ᵏ","l":"ˡ","m":"ᵐ","n":"ⁿ","o":"ᵒ","p":"ᵖ","r":"ʳ","s":"ˢ","t":"ᵗ","u":"ᵘ",
	"v":"ᵛ","w":"ʷ","x":"ˣ","y":"ʸ","z":"ᶻ",
	"A":"ᴬ","B":"ᴮ","D":"ᴰ","E":"ᴱ","G":"ᴳ","H":"ᴴ","I":"ᴵ","J":"ᴶ","K":"ᴷ","L":"ᴸ",
	"M":"ᴹ","N":"ᴺ","O":"ᴼ","P":"ᴾ","R":"ᴿ","T":"ᵀ","U":"ᵁ","V":"ⱽ","W":"ᵂ"
};

// Subscript mapping (Unicode limited)
const subscriptMap: Record<string, string> = {
	"0": "₀","1": "₁","2": "₂","3": "₃","4": "₄","5": "₅","6": "₆","7": "₇","8": "₈","9": "₉",
	"+": "₊","-": "₋","=": "₌","(": "₍",")": "₎","a": "ₐ","e": "ₑ",	"h": "ₕ","i": "ᵢ","j": "ⱼ","k": "ₖ","l": "ₗ","m": "ₘ","n": "ₙ",
	"o": "ₒ","p": "ₚ","r": "ᵣ","s": "ₛ","t": "ₜ","u": "ᵤ","v": "ᵥ","x": "ₓ",

	// A–Z (vetëm aty ku ekziston variant **identik Unicode** – SPOILER: nuk ka uppercase subscript në Unicode)
	// prandaj NUKE FUTIM ASNJË uppercase këtu
};

// Reverse maps for toggle
const reverseSupMap = Object.fromEntries(Object.entries(superscriptMap).map(([k,v]) => [v,k]));
const reverseSubMap = Object.fromEntries(Object.entries(subscriptMap).map(([k,v]) => [v,k]));

// Detect shortcuts
const isSupShortcut = (e: KeyboardEvent) =>
	e.ctrlKey && (e.key === '.' || e.code === 'Period' || e.code === 'NumpadDecimal');

const isSubShortcut = (e: KeyboardEvent) =>
	e.ctrlKey && (e.key === ',' || e.code === 'Comma');

const setCursor = (el: HTMLInputElement, pos: number) => {
	setTimeout(() => { try { el.setSelectionRange(pos, pos); } catch {} }, 0);
};

const handleKeydown = (e: KeyboardEvent) => {
	const input = e.target as HTMLInputElement;
	if (!input) return;
	if (!isSupShortcut(e) && !isSubShortcut(e)) return;

	const cursorPos = input.selectionStart ?? 0;
	if (cursorPos <= 0) return;

	const char = stringContent.value[cursorPos - 1];
	if (!char) return;

	// --------- Ctrl + . (Superscript Toggle + X2 Rule) ---------
	if (isSupShortcut(e)) {
		e.preventDefault();

		// Subscript → Superscript
		if (reverseSubMap[char]) {
			const normal = reverseSubMap[char];
			if (superscriptMap[normal]) {
				stringContent.value = stringContent.value.slice(0, cursorPos - 1) + superscriptMap[normal] + stringContent.value.slice(cursorPos);
			}
			else {
				stringContent.value = stringContent.value.slice(0, cursorPos - 1) + normal + stringContent.value.slice(cursorPos);
			}
			setCursor(input, cursorPos);
			return;
		}

		// Superscript → Normal
		if (reverseSupMap[char]) {
			stringContent.value = stringContent.value.slice(0, cursorPos - 1) + reverseSupMap[char] + stringContent.value.slice(cursorPos);
			setCursor(input, cursorPos);
			return;
		}

		// Normal → Superscript
		if (superscriptMap[char]) {
			stringContent.value = stringContent.value.slice(0, cursorPos - 1) + superscriptMap[char] + stringContent.value.slice(cursorPos);
			setCursor(input, cursorPos);
			return;
		}
	}

	// --------- Ctrl + , (Subscript Toggle + X2 Rule) ---------
	if (isSubShortcut(e)) {
		e.preventDefault();

		// Superscript → Subscript
		if (reverseSupMap[char]) {
			const normal = reverseSupMap[char];
			if (subscriptMap[normal]) {
				stringContent.value = stringContent.value.slice(0, cursorPos - 1) + subscriptMap[normal] + stringContent.value.slice(cursorPos);
			}
			else {
				stringContent.value = stringContent.value.slice(0, cursorPos - 1) + normal + stringContent.value.slice(cursorPos);
			}
			setCursor(input, cursorPos);
			return;
		}

		// Subscript → Normal
		if (reverseSubMap[char]) {
			stringContent.value = stringContent.value.slice(0, cursorPos - 1) + reverseSubMap[char] + stringContent.value.slice(cursorPos);
			setCursor(input, cursorPos);
			return;
		}

		// Normal → Subscript
		if (subscriptMap[char]) {
			stringContent.value = stringContent.value.slice(0, cursorPos - 1) + subscriptMap[char] + stringContent.value.slice(cursorPos);
			setCursor(input, cursorPos);
			return;
		}
	}
};

const attachKeyListener = () => {
	if (props.inputType === 'input' || props.inputType === 'packageTitle'  || props.inputType === 'textarea' || props.inputType === 'editor' || props.inputType === 'references' || props.inputType === 'quiz') {
		const el = inputElement.value?.$el?.querySelector('input, textarea');
		if (el && !el._superSubBound) {
			el.addEventListener('keydown', handleKeydown);
			el._superSubBound = true;
		}
	}
};

onMounted(attachKeyListener);
onUpdated(attachKeyListener);

onBeforeUnmount(() => {
	const el = inputElement.value?.$el?.querySelector('input, textarea');
	if (el) el.removeEventListener('keydown', handleKeydown);
});
</script>



<style>
.ql-editor ul{padding-left:0!important;}
</style>